'use client';

import React, { useState } from 'react';

interface Review {
  id: number;
  guest: string;
  guestAvatar: string;
  property: string;
  host: string;
  rating: number;
  comment: string;
  date: string;
  status: 'pending' | 'approved' | 'rejected' | 'flagged';
  flag?: string;
  category: 'cleanliness' | 'communication' | 'accuracy' | 'value' | 'general';
  reportCount: number;
}

const initialReviews: Review[] = [
  { id: 1, guest: 'Sarah Nakato', guestAvatar: 'S', property: 'Safari Lodge Murchison', host: 'David Ochieng', rating: 5, comment: 'Absolutely breathtaking experience! Woke up to elephants outside our window. The staff were incredibly attentive and the food was outstanding.', date: 'Aug 15, 2026', status: 'pending', category: 'general', reportCount: 0 },
  { id: 2, guest: 'Moses Kato', guestAvatar: 'M', property: 'City Apartment Kampala', host: 'Amina Hassan', rating: 1, comment: 'Terrible experience. Property was nothing like the photos. Host was unresponsive for 12 hours. Demand a refund.', date: 'Aug 14, 2026', status: 'flagged', flag: 'Potential fraud claim', category: 'accuracy', reportCount: 3 },
  { id: 3, guest: 'Peter Mugisha', guestAvatar: 'P', property: 'Luxury Villa Kololo', host: 'James Ssekandi', rating: 4, comment: 'Beautiful villa, great amenities. Host was very accommodating and responded quickly to all our requests.', date: 'Aug 12, 2026', status: 'approved', category: 'communication', reportCount: 0 },
  { id: 4, guest: 'Grace Achieng', guestAvatar: 'G', property: 'Lakeside Retreat Entebbe', host: 'Grace Achieng', rating: 2, comment: 'Disappointing. The lake view was blocked by construction. Not worth the price at all.', date: 'Aug 10, 2026', status: 'flagged', flag: 'Host dispute', category: 'value', reportCount: 1 },
  { id: 5, guest: 'Ruth Kemigisha', guestAvatar: 'R', property: 'Mountain Cabin Rwenzori', host: 'Ruth Kemigisha', rating: 5, comment: 'Magical mountain retreat. Perfect for nature lovers. The sunrise views were absolutely stunning.', date: 'Aug 8, 2026', status: 'pending', category: 'general', reportCount: 0 },
  { id: 6, guest: 'John Otieno', guestAvatar: 'J', property: 'Bwindi Forest Cottage', host: 'Mary Namukasa', rating: 3, comment: 'Average stay. The cottage was clean but smaller than expected. Location was great though.', date: 'Aug 6, 2026', status: 'pending', category: 'cleanliness', reportCount: 0 },
  { id: 7, guest: 'Fatuma Wanjiru', guestAvatar: 'F', property: 'Nile View Apartment', host: 'Hassan Mwangi', rating: 1, comment: 'This review contains inappropriate language and personal attacks on the host.', date: 'Aug 4, 2026', status: 'flagged', flag: 'Inappropriate content', category: 'general', reportCount: 5 },
];

const statusConfig: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  pending: { label: 'Pending', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400' },
  approved: { label: 'Approved', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  rejected: { label: 'Rejected', bg: 'bg-gray-100', text: 'text-gray-500', dot: 'bg-gray-400' },
  flagged: { label: 'Flagged', bg: 'bg-red-50', text: 'text-red-600', dot: 'bg-red-500' },
};

const categoryLabels: Record<string, string> = {
  cleanliness: '🧹 Cleanliness',
  communication: '💬 Communication',
  accuracy: '📍 Accuracy',
  value: '💰 Value',
  general: '⭐ General',
};

export default function AdminReviewsModeration() {
  const [reviews, setReviews] = useState<Review[]>(initialReviews);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected' | 'flagged'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'rating' | 'reports'>('date');
  const [searchQuery, setSearchQuery] = useState('');

  const handleAction = (id: number, action: 'approved' | 'rejected' | 'flagged') => {
    setReviews(prev => prev.map(r => r.id === id ? { ...r, status: action } : r));
    if (selectedReview?.id === id) {
      setSelectedReview(prev => prev ? { ...prev, status: action } : null);
    }
  };

  const handleBulkApprove = () => {
    setReviews(prev => prev.map(r => r.status === 'pending' ? { ...r, status: 'approved' } : r));
  };

  const filtered = reviews
    .filter(r => filter === 'all' || r.status === filter)
    .filter(r => categoryFilter === 'all' || r.category === categoryFilter)
    .filter(r => !searchQuery || r.guest.toLowerCase().includes(searchQuery.toLowerCase()) || r.property.toLowerCase().includes(searchQuery.toLowerCase()) || r.comment.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === 'rating') return a.rating - b.rating;
      if (sortBy === 'reports') return b.reportCount - a.reportCount;
      return 0;
    });

  const counts = {
    all: reviews.length,
    pending: reviews.filter(r => r.status === 'pending').length,
    approved: reviews.filter(r => r.status === 'approved').length,
    rejected: reviews.filter(r => r.status === 'rejected').length,
    flagged: reviews.filter(r => r.status === 'flagged').length,
  };

  const avgRating = (reviews.filter(r => r.status === 'approved').reduce((s, r) => s + r.rating, 0) / Math.max(reviews.filter(r => r.status === 'approved').length, 1)).toFixed(1);

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Total Reviews', value: counts.all.toString(), color: 'text-gray-900', bg: 'bg-white' },
          { label: 'Pending', value: counts.pending.toString(), color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Approved', value: counts.approved.toString(), color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Flagged', value: counts.flagged.toString(), color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Avg Rating', value: avgRating, color: 'text-amber-500', bg: 'bg-white' },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} rounded-2xl p-4 border border-gray-100 shadow-sm`}>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        {/* Search */}
        <div className="relative flex-1">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search reviews, guests, properties..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40"
          />
        </div>
        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'date' | 'rating' | 'reports')}
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40"
        >
          <option value="date">Sort: Date</option>
          <option value="rating">Sort: Rating</option>
          <option value="reports">Sort: Most Reported</option>
        </select>
        {counts.pending > 0 && (
          <button
            onClick={handleBulkApprove}
            className="px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold rounded-xl transition-colors whitespace-nowrap"
          >
            ✅ Approve All Pending ({counts.pending})
          </button>
        )}
      </div>

      {/* Status Filters */}
      <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
        {(['all', 'pending', 'approved', 'flagged', 'rejected'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold capitalize whitespace-nowrap transition-all ${
              filter === f ? 'bg-[#4088C6] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#4088C6]/40'
            }`}
          >
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${filter === f ? 'bg-white/25 text-white' : 'bg-gray-100 text-gray-600'}`}>
              {counts[f]}
            </span>
          </button>
        ))}
      </div>

      {/* Category Filters */}
      <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
        <button
          onClick={() => setCategoryFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${categoryFilter === 'all' ? 'bg-gray-800 text-white' : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-400'}`}
        >
          All Categories
        </button>
        {Object.entries(categoryLabels).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setCategoryFilter(key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${categoryFilter === key ? 'bg-gray-800 text-white' : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-400'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Reviews List */}
      <div className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
            <div className="text-4xl mb-3">🔍</div>
            <p className="font-semibold text-gray-600">No reviews match your filters</p>
          </div>
        )}
        {filtered.map((review) => {
          const sc = statusConfig[review.status];
          return (
            <div
              key={review.id}
              className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${
                review.status === 'flagged' ? 'border-amber-200' : review.status === 'pending' ? 'border-amber-200' : 'border-gray-100'
              }`}
            >
              <div className="p-5">
                {/* Flag Banner */}
                {review.status === 'flagged' && review.flag && (
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-4">
                    <span>⚠️</span>
                    <span className="text-xs font-semibold text-amber-700">Flagged: {review.flag}</span>
                    {review.reportCount > 0 && (
                      <span className="ml-auto text-xs bg-amber-100 text-amber-600 font-bold px-2 py-0.5 rounded-full">
                        {review.reportCount} report{review.reportCount > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}

                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4088C6] to-[#3070a6] flex items-center justify-center text-white font-bold flex-shrink-0">
                    {review.guestAvatar}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-bold text-gray-900">{review.guest}</p>
                          <span className="text-gray-300">·</span>
                          <p className="text-xs text-gray-500">{review.property}</p>
                          <span className="text-gray-300">·</span>
                          <p className="text-xs text-gray-400">{review.date}</p>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <span key={s} className={`text-sm ${s <= review.rating ? 'text-amber-400' : 'text-gray-200'}`}>★</span>
                            ))}
                          </div>
                          <span className="text-xs text-gray-400">{categoryLabels[review.category]}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${sc.bg} ${sc.text}`}>
                          <div className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                          {sc.label}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-700 leading-relaxed mb-3">{review.comment}</p>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {review.status !== 'approved' && (
                        <button
                          onClick={() => handleAction(review.id, 'approved')}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-600 text-xs font-semibold rounded-lg transition-colors"
                        >
                          ✅ Approve
                        </button>
                      )}
                      {review.status !== 'rejected' && (
                        <button
                          onClick={() => handleAction(review.id, 'rejected')}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-600 text-xs font-semibold rounded-lg transition-colors"
                        >
                          🚫 Reject
                        </button>
                      )}
                      {review.status !== 'flagged' && (
                        <button
                          onClick={() => handleAction(review.id, 'flagged')}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 border border-red-200 text-red-600 text-xs font-semibold rounded-lg transition-colors"
                        >
                          🚩 Flag
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedReview(selectedReview?.id === review.id ? null : review)}
                        className="ml-auto text-xs text-gray-400 hover:text-gray-600 font-medium transition-colors"
                      >
                        {selectedReview?.id === review.id ? 'Hide details ↑' : 'View details ↓'}
                      </button>
                    </div>

                    {/* Expanded Details */}
                    {selectedReview?.id === review.id && (
                      <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs font-semibold text-gray-500 mb-1">Host</p>
                          <p className="text-sm font-semibold text-gray-800">{review.host}</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs font-semibold text-gray-500 mb-1">Report Count</p>
                          <p className="text-sm font-semibold text-gray-800">{review.reportCount} report{review.reportCount !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs font-semibold text-gray-500 mb-1">Category</p>
                          <p className="text-sm font-semibold text-gray-800">{categoryLabels[review.category]}</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3">
                          <p className="text-xs font-semibold text-gray-500 mb-1">Current Status</p>
                          <p className={`text-sm font-semibold ${sc.text}`}>{sc.label}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
